import React from 'react';
import { Box, Typography, Grid, Link, Divider, Stack, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Email,
  Phone,
  LinkedIn,
  Twitter,
  Facebook,
  Instagram
} from '@mui/icons-material';
import Services from './Services';
import { useThemeMode } from '../contexts/ThemeContext';
import BrandLogo from './BrandLogo';

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const isRTL = i18n.language === 'ar';
  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
        backgroundColor: isDark ? '#0a0f1a' : 'oklch(96.5% 0.026 102.212)',
        backdropFilter: 'blur(20px)',
        borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.5), transparent)',
          animation: 'shimmer 3s infinite',
        },
        '@keyframes shimmer': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}
    >
       {/* Services Section */}
          <Box >
            <Services />
          </Box>
      <Box sx={{ py: 6, px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Logo and About Section */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none',
              mb: 2,
              transition: 'transform 0.3s ease, filter 0.3s ease',
              '&:hover': {
                transform: 'scale(1.03)',
                filter: 'drop-shadow(0 0 18px rgba(0, 71, 255, 0.18))',
              },
            }}
          >
            <Box sx={{ width: { xs: 220, sm: 280 } }}>
              <BrandLogo showTagline />
            </Box>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.8,
            }}
          >
            {t('footer.aboutText', { defaultValue: 'Your trusted partner in medical equipment. Providing high-quality refurbished medical devices with comprehensive warranties and exceptional service.' })}
          </Typography>
        </Box>

        {/* Main Footer Content - 4 Columns */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Catalog Column */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#00d4ff',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  [isRTL ? 'right' : 'left']: 0,
                  width: 40,
                  height: 3,
                  bgcolor: '#00d4ff',
                  borderRadius: 0.5,
                },
              }}
            >
              {t('footer.catalog', { defaultValue: 'Catalog' })}
            </Typography>
            <Stack spacing={1.5}>
              <Link
                component={RouterLink}
                to="/products"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('common.search')}
              </Link>
              <Link
                component={RouterLink}
                to="/brands"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.ourBrands', { defaultValue: 'Our Brands' })}
              </Link>
              <Link
                component={RouterLink}
                to="/offers"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.ourOffers', { defaultValue: 'Our Offers Without Product Sheet' })}
              </Link>
            </Stack>
          </Grid>

          {/* About Column */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#00d4ff',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  [isRTL ? 'right' : 'left']: 0,
                  width: 40,
                  height: 3,
                  bgcolor: '#00d4ff',
                  borderRadius: 0.5,
                },
              }}
            >
              {t('footer.aboutUs')}
            </Typography>
            <Stack spacing={1.5}>
              <Link
                component={RouterLink}
                to="/about"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.aboutUs')}
              </Link>
              <Link
                component={RouterLink}
                to="/services"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.servicesAndBenefits', { defaultValue: "Zetta Med's Services and Benefits" })}
              </Link>
              <Link
                component={RouterLink}
                to="/sell"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.sellEquipment', { defaultValue: 'Sell Your Equipment' })}
              </Link>
            </Stack>
          </Grid>

          {/* Help Column */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#00d4ff',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  [isRTL ? 'right' : 'left']: 0,
                  width: 40,
                  height: 3,
                  bgcolor: '#00d4ff',
                  borderRadius: 0.5,
                },
              }}
            >
              {t('footer.help', { defaultValue: 'Help' })}
            </Typography>
            <Stack spacing={1.5}>
              <Link
                component={RouterLink}
                to="/terms"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.termsOfUse', { defaultValue: 'Terms and Conditions of Use' })}
              </Link>
              <Link
                component={RouterLink}
                to="/sales-terms"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.salesTerms', { defaultValue: 'General Terms and Conditions of Sale' })}
              </Link>
              <Link
                component={RouterLink}
                to="/privacy"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.legalNotices', { defaultValue: 'Legal Notices and Privacy Policy' })}
              </Link>
              <Link
                component={RouterLink}
                to="/contact"
                sx={{
                  color: isDark ? 'rgba(240,244,248,0.7)' : 'rgba(0,0,0,0.7)',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    color: '#00d4ff',
                    transform: isRTL ? 'translateX(-5px)' : 'translateX(5px)',
                  },
                }}
              >
                {t('footer.contact')}
              </Link>
            </Stack>
          </Grid>

          {/* Contact Column */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                mb: 3,
                color: '#00d4ff',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  [isRTL ? 'right' : 'left']: 0,
                  width: 40,
                  height: 3,
                  bgcolor: '#00d4ff',
                  borderRadius: 0.5,
                },
              }}
            >
              {t('footer.contact')}
            </Typography>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  bgcolor: 'rgba(0,212,255,0.08)',
                  borderRadius: 1,
                  border: '1px solid rgba(0,212,255,0.2)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    boxShadow: '0 0 20px rgba(0,212,255,0.2)',
                  },
                }}
              >
                <Email sx={{ color: '#00d4ff' }} />
                <Link
                  href="mailto:contact@zettamed.fr"
                  sx={{
                    color: isDark ? 'rgba(240,244,248,0.8)' : 'rgba(0,0,0,0.8)',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'color 0.3s',
                    '&:hover': {
                      color: '#00d4ff',
                    },
                  }}
                >
                  contact@zettamed.fr
                </Link>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  bgcolor: 'rgba(0,212,255,0.08)',
                  borderRadius: 1,
                  border: '1px solid rgba(0,212,255,0.2)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: '#00d4ff',
                    boxShadow: '0 0 20px rgba(0,212,255,0.2)',
                  },
                }}
              >
                <Phone sx={{ color: '#00d4ff' }} />
                <Link
                  href="tel:+33769609964"
                  sx={{
                    color: isDark ? 'rgba(240,244,248,0.8)' : 'rgba(0,0,0,0.8)',
                    textDecoration: 'none',
                    fontWeight: 500,
                    transition: 'color 0.3s',
                    '&:hover': {
                      color: '#00d4ff',
                    },
                  }}
                >
                 +565 946 23 232
                </Link>
              </Box>

              {/* Social Media Icons */}
              <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'rgba(0,119,181,0.1)',
                    border: '1px solid rgba(0,119,181,0.2)',
                    color: '#0077B5',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: '#0077B5',
                      color: 'white',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,119,181,0.4)',
                    },
                  }}
                >
                  <LinkedIn fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'rgba(29,161,242,0.1)',
                    border: '1px solid rgba(29,161,242,0.2)',
                    color: '#1DA1F2',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: '#1DA1F2',
                      color: 'white',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(29,161,242,0.4)',
                    },
                  }}
                >
                  <Twitter fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'rgba(24,119,242,0.1)',
                    border: '1px solid rgba(24,119,242,0.2)',
                    color: '#1877F2',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: '#1877F2',
                      color: 'white',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(24,119,242,0.4)',
                    },
                  }}
                >
                  <Facebook fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    bgcolor: 'rgba(228,64,95,0.1)',
                    border: '1px solid rgba(228,64,95,0.2)',
                    color: '#E4405F',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: '#E4405F',
                      color: 'white',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(228,64,95,0.4)',
                    },
                  }}
                >
                  <Instagram fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider
          sx={{
            borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            mb: 4,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 100,
              height: 1,
              background: 'linear-gradient(90deg, transparent, rgba(0,212,255,0.5), transparent)',
            },
          }}
        />

        {/* Bottom Footer */}
        <Box
          sx={{
            pt: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isDark ? 'rgba(240,244,248,0.6)' : 'rgba(0,0,0,0.6)',
              textAlign: { xs: 'center', md: 'left' },
            }}
          >
            © {new Date().getFullYear()} Zetta Med Platform. {t('footer.allRightsReserved')}
          </Typography>
          
          <Stack
            direction="row"
            spacing={3}
            sx={{
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', md: 'flex-end' },
              gap: { xs: 1, sm: 3 },
            }}
          >
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: isDark ? 'rgba(240,244,248,0.6)' : 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s',
                '&:hover': {
                  color: '#00d4ff',
                },
              }}
            >
              {t('footer.privacyPolicy')}
            </Link>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: isDark ? 'rgba(240,244,248,0.6)' : 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s',
                '&:hover': {
                  color: '#00d4ff',
                },
              }}
            >
              {t('footer.termsOfService')}
            </Link>
            <Link
              component={RouterLink}
              to="/cookies"
              sx={{
                color: isDark ? 'rgba(240,244,248,0.6)' : 'rgba(0,0,0,0.6)',
                textDecoration: 'none',
                fontSize: '0.875rem',
                transition: 'color 0.3s',
                '&:hover': {
                  color: '#00d4ff',
                },
              }}
            >
              {t('footer.cookiePolicy', { defaultValue: 'Cookie Policy' })}
            </Link>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;